import React, { useContext, useState, useMemo } from 'react';
import { Stack, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch } from '@mui/material';
import PackMonitoringTable from './PackMonitoring/GenericPackTable';
import { useGetPackMonitoringDataQuery, useGetOrganizationPacksForMonitoringQuery } from '../../api/campaignMonitoringSlice';
import LoadingIndicator from '../LoadingIndicator';
import PackDropdown from './PackMonitoring/PackDropdown';
import ProcessOverviewContent from './PackMonitoring/ProcessOverviewContent';
import { useLogUserActionMutation } from '../../api/apiSlice';
import { UserContext } from '../Hooks/useUser';
import Filter from '../CustomComponents/Filter';
import ToggleButtons from '../CustomComponents/ToggleButtons';
import { theme } from '../../theme';

function PackMonitoring() {
    const [selectedPack, setSelectedPack] = useState('');
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [selectedSpecialties, setSelectedSpecialties] = useState([]);
    const [viewMode, setViewMode] = useState('standard');
    const [showHighWasteOnly, setShowHighWasteOnly] = useState(false);
    const [isProcessOverviewOpen, setIsProcessOverviewOpen] = useState(false);
    const { user } = useContext(UserContext);
    const [logUserAction] = useLogUserActionMutation();

    const { data: packList } = useGetOrganizationPacksForMonitoringQuery(user.hospital.organization_id);

    // Skip query if no pack is selected
    const { data: packData, error, isFetching } = useGetPackMonitoringDataQuery(selectedPack, {
        skip: !selectedPack || selectedPack === ''
    });

    function handlePackChange(packId) {
        setSelectedPack(packId);
        setSelectedItemId(null);
        setSelectedSpecialties([]);
        setShowHighWasteOnly(false);
        logUserAction({ action: 'pack_monitoring_pack_selected', data: { packId } });
    }

    function handleItemSelect(itemId) {
        if (selectedItemId === itemId) {
            // If already selected, deselect
            setSelectedItemId(null);
            logUserAction({ action: 'pack_monitoring_item_deselected', data: { packId: selectedPack, itemId } });
            return;
        }
        setSelectedItemId(itemId);
        logUserAction({ action: 'pack_monitoring_item_selected', data: { packId: selectedPack, itemId } });
    }

    function handleSpecialtyChange(specialties) {
        setSelectedSpecialties(specialties);
        logUserAction({ action: 'pack_monitoring_specialty_filtered', data: { packId: selectedPack, specialties } });
    }

    // Get unique specialties from physician data
    const availableSpecialties = useMemo(() => {
        if (!packData?.physicians) return [];
        const physiciansArray = Object.values(packData.physicians);
        const specialties = physiciansArray.map(physician => physician.specialty).filter(Boolean);
        return [...new Set(specialties)].sort();
    }, [packData?.physicians]);

    // Filter physicians by selected specialties
    const filteredPhysicians = useMemo(() => {
        if (!packData?.physicians) return [];
        const physiciansArray = Object.values(packData.physicians);
        if (selectedSpecialties.length === 0) return physiciansArray;
        return physiciansArray.filter(physician => 
            selectedSpecialties.includes(physician.specialty)
        );
    }, [packData?.physicians, selectedSpecialties]);

    // Filter pack items by high waste estimate
    const filteredPackItems = useMemo(() => {
        if (!packData?.pack_items) return [];
        if (!showHighWasteOnly) return packData.pack_items;
        return packData.pack_items.filter(item => (item.waste_estimate?.low_estimate ?? 0) >= 0.05);
    }, [packData?.pack_items, showHighWasteOnly]);

    return (
        <Stack pb={4} spacing={3}>
            <Stack direction="row" px={4} pb={1} spacing={1} sx={{ top: 64, backgroundColor: theme.palette.background.white, position: 'sticky', zIndex: 5, alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack width='50%' spacing={1}>
                    <Typography variant="heavy" sx={{ fontSize: '24px' }}>
                        Physician Pack Review Monitoring
                    </Typography>
                    <Stack spacing={2} direction="row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <PackDropdown packs={packList || []} selectedPack={selectedPack} onSelectPack={handlePackChange} />
                        {selectedPack && selectedPack !== '' && packData && !isFetching && (
                            <Filter
                                options={availableSpecialties}
                                value={selectedSpecialties}
                                onSelect={handleSpecialtyChange}
                                label="Filter by Specialty"
                                placeholder={selectedSpecialties.length === 0 
                                    ? 'Showing all specialties'
                                    : `Showing ${selectedSpecialties.length} of ${availableSpecialties.length} specialties`
                                }
                                multiple
                            />
                        )}
                    </Stack>
                </Stack>
                {selectedPack && selectedPack !== '' && (
                    <Stack direction='row' spacing={2}>
                        <ToggleButtons
                            options={[{ label: 'Standard', value: 'standard' }, { label: 'Periop', value: 'periop' }]}
                            toggleValue={viewMode}
                            setToggleValue={setViewMode}
                        />
                        <FormControlLabel
                            control={<Switch checked={showHighWasteOnly} onChange={(e) => setShowHighWasteOnly(e.target.checked)} />}
                            label="High waste only (≥5%)"
                        />    
                        <Button variant="outlined" onClick={() => setIsProcessOverviewOpen(true)}>
                            Process Overview
                        </Button>
                    </Stack>
                )}
            </Stack>

            {(!selectedPack || selectedPack === '') && (
                <Stack px={3} direction="row" spacing={3} sx={{ width: '100%' }}>
                    <Stack spacing={2} sx={{ width: '50%' }}>
                        <Typography variant="normal" sx={{ color: 'text.secondary' }}>
                            Select a pack from the dropdown above to view usage estimates from physicians -- packs with the most feedback collected from physicians are listed at the top of the dropdown.
                        </Typography>
                    </Stack>
                    <Box sx={{ width: '50%', maxHeight: '70vh', overflowY: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 3, backgroundColor: 'background.white' }}>
                        <Typography variant="heavy" sx={{ fontSize: '18px', mb: 1 }}>
                            Physician Pack Review Process
                        </Typography>
                        <ProcessOverviewContent />
                    </Box>
                </Stack>
            )}
            
            {(isFetching) && (
                <LoadingIndicator />
            )}
            
            {selectedPack && selectedPack !== '' && error && !isFetching && (
                <Typography color="error">Error loading pack data: {error.message || 'Unknown error'}</Typography>
            )}
            
            {selectedPack && selectedPack !== '' && packData && !isFetching && (
                <Stack px={3} spacing={3} direction="row" sx={{ width: '100%' }}>
                    <Box sx={{ width: '55%' }}>
                        <PackMonitoringTable 
                            data={filteredPackItems} 
                            tableType="items"
                            title="Pack Items"
                            selectedItemId={selectedItemId}
                            onItemSelect={handleItemSelect}
                            viewMode={viewMode}
                        />
                    </Box>
                    <Box sx={{ width: '45%' }}>
                        <PackMonitoringTable 
                            data={filteredPhysicians} 
                            tableType="physicians"
                            title="Physicians"
                            selectedItemId={selectedItemId}
                            viewMode={viewMode}
                        />
                    </Box>
                </Stack>
            )}

            <Dialog open={isProcessOverviewOpen} onClose={() => setIsProcessOverviewOpen(false)} maxWidth="sm">
                <DialogTitle>
                    Physician Pack Review Process
                </DialogTitle>
                <DialogContent dividers>
                    <ProcessOverviewContent />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setIsProcessOverviewOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

export default PackMonitoring;